import { DEDUPE_OVERLAP } from '@/constants/app';
import type { Item, Session } from './types';

/**
 * Cumulative review must not ask the same thing twice when a concept appears
 * both in a class and in a chapter review. Text is normalised, word sets are
 * compared, and the chapter-review copy is the one kept.
 */

export function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function wordSet(text: string): Set<string> {
  const words = normalizeText(text).split(' ').filter(Boolean);
  return new Set(words);
}

/**
 * Jaccard index: shared words over total distinct words. Reading "overlap by at
 * least 85%" this way is the conservative one — a looser measure would treat a
 * short question as a duplicate of any longer one containing its words, and
 * dropping real material is worse than the occasional near-repeat.
 */
export function overlap(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let shared = 0;
  for (const word of a) if (b.has(word)) shared += 1;
  const union = a.size + b.size - shared;
  return union === 0 ? 0 : shared / union;
}

function comparableText(item: Item): string {
  return item.type === 'flashcard' ? (item.front ?? '') : (item.q ?? '');
}

export interface DedupeResult {
  kept: Item[];
  /** id of the dropped item -> id of the one kept in its place. */
  dropped: Map<string, string>;
}

export function dedupeForCumulative(items: Item[], sessions: Session[]): DedupeResult {
  const kindById = new Map(sessions.map((s) => [s.id, s.kind]));

  // Which copy survives a clash is decided here. A chapter review is the
  // professor's own summary of what matters, a class is what he was actually
  // taught, and a textbook chapter is neither — so it yields to both.
  const rank = (item: Item) => {
    switch (kindById.get(item.sessionId)) {
      case 'chapter':
        return 0;
      case 'class':
        return 1;
      default:
        return 2;
    }
  };

  const ordered = [...items].sort((a, b) => rank(a) - rank(b));

  const kept: Item[] = [];
  const keptSets: Array<{ item: Item; words: Set<string> }> = [];
  const dropped = new Map<string, string>();

  for (const item of ordered) {
    const words = wordSet(comparableText(item));
    const clash = keptSets.find(
      (candidate) =>
        candidate.item.type === item.type && overlap(candidate.words, words) >= DEDUPE_OVERLAP,
    );
    if (clash) {
      dropped.set(item.id, clash.item.id);
      continue;
    }
    kept.push(item);
    keptSets.push({ item, words });
  }

  // Hand back the caller's original ordering, minus the drops.
  const keepIds = new Set(kept.map((i) => i.id));
  return { kept: items.filter((i) => keepIds.has(i.id)), dropped };
}
